import { z } from "zod";
import { AuditTrailEntryType } from "../AuditTrailEntryType.js";
// Work / admission / de-minimis / redo audit `data` schemas. See `registry.ts` for
// the audit-data leniency contract these follow.
const requestReasonSchema = z.object({
    label: z.string(),
    value: z.string(),
});
// Shared empty payload (lifecycle transitions that audit only the resources).
export const emptyWorkDataSchema = z.object({});
// ===== Work lifecycle =====
export const createWorkRequestDataSchema = z.object({
    assignedUserId: z.string().nullish(),
    assignedUserName: z.string().nullish(),
    teamId: z.string().nullish(),
    teamName: z.string().nullish(),
    requestType: z.string().nullish(),
    formId: z.string().nullish(),
    formPart: z.number().nullish(),
});
export const createAmendRequestDataSchema = z.object({
    assignedUserId: z.string().nullish(),
    assignedUserName: z.string().nullish(),
    teamId: z.string().nullish(),
    teamName: z.string().nullish(),
    requestType: z.string().nullish(),
    requestReasons: z.array(requestReasonSchema).nullish(),
    requestAdditionalInfo: z.string().nullish(),
    formId: z.string().nullish(),
    formPart: z.number().nullish(),
});
export const declineWorkRequestDataSchema = z.object({
    reason: z.string().nullish(),
    reasonDescription: z.string().nullish(), // PII
    requestType: z.string().nullish(),
    type: z.string().nullish(), // "decline" | "cancel"
});
export const acceptWorkRequestDataSchema = z.object({
    requestType: z.string().nullish(),
    formId: z.string().nullish(),
    formPart: z.number().nullish(),
});
export const archiveWorkRequestDataSchema = z.object({
    bulk: z.boolean().nullish(),
    count: z.number().nullish(),
});
export const claimWorkRequestDataSchema = z.object({
    assignedUserId: z.string().nullish(),
    assignedUserName: z.string().nullish(),
    teamId: z.string().nullish(),
    teamName: z.string().nullish(),
});
export const unfinaliseWorkRequestDataSchema = z.object({
    reason: z.string().nullish(),
    reasonDescription: z.string().nullish(), // PII
});
export const cancelAmendRequestDataSchema = z.object({
    reason: z.string().nullish(),
});
export const expireAmendRequestDataSchema = z.object({
    expiryDateTime: z.string().nullish(),
    // Required: the whole rendered message is the rectification period.
    expiryPeriod: z.object({ value: z.number(), unit: z.string() }),
    formTemplate: z.object({ id: z.string(), version: z.string() }).nullish(),
    formId: z.string().nullish(),
});
export const autoRouteToMhlTeamDataSchema = z.object({
    teamId: z.string().nullish(),
    teamName: z.string().nullish(),
    formTemplateId: z.string().nullish(),
});
export const adminDeleteWorkItemDataSchema = z.object({
    reason: z.string().nullish(),
    ticketNumber: z.string().nullish(),
});
export const createFileNoteRequestDataSchema = z.object({
    fileNote: z.string().nullish(), // PII
    fileNoteCreatedBy: z.string().nullish(),
    formId: z.string().nullish(),
});
export const deleteFileNoteRequestDataSchema = z.object({
    deletedFileNoteIds: z.string().nullish(),
    deleteFileNote: z.array(z.string()).nullish(), // PII
    formId: z.string().nullish(),
});
// ===== De minimis corrections =====
export const createDeMinimisRequestDataSchema = z.object({
    assignedUserId: z.string().nullish(),
    assignedUserName: z.string().nullish(),
    teamId: z.string().nullish(),
    teamName: z.string().nullish(),
    requestType: z.string().nullish(),
    errorType: z.string().nullish(),
    errorTypeLabel: z.string().nullish(),
    formId: z.string().nullish(),
    formPart: z.number().nullish(),
    fileNoteId: z.number().nullish(),
    note: z.string().nullish(),
    selectedFields: z.array(z.string()).nullish(),
});
export const acceptDeMinimisRequestDataSchema = z.object({
    fileNoteId: z.number().nullish(),
    errorType: z.string().nullish(),
    targetUserId: z.string().nullish(),
    targetFormPart: z.number().nullish(),
});
export const completeDeMinimisRequestDataSchema = z.object({
    fileNoteId: z.number().nullish(),
    signatoryName: z.string().nullish(),
    response: z.string().nullish(),
    errorType: z.string().nullish(),
    targetUserId: z.string().nullish(),
    targetFormPart: z.number().nullish(),
});
export const rejectDeMinimisRequestDataSchema = z.object({
    fileNoteId: z.number().nullish(),
    rejectedReason: z.string().nullish(),
    errorType: z.string().nullish(),
    targetUserId: z.string().nullish(),
    targetFormPart: z.number().nullish(),
});
export const cancelDeMinimisRequestDataSchema = z.object({
    fileNoteId: z.number().nullish(),
    reason: z.string().nullish(),
    errorType: z.string().nullish(),
    targetUserId: z.string().nullish(),
    targetFormPart: z.number().nullish(),
});
// ===== Redo admission forms (non-s15) =====
// createRedoRequest and initiateRedo share a base shape (initiate adds newFormId).
const redoCommonDataSchema = z.object({
    assignedUserId: z.string().nullish(),
    assignedUserName: z.string().nullish(),
    teamId: z.string().nullish(),
    teamName: z.string().nullish(),
    requestType: z.string().nullish(),
    requestReasons: z.array(requestReasonSchema).nullish(),
    requestAdditionalInfo: z.string().nullish(),
    formId: z.string().nullish(),
});
export const createRedoRequestDataSchema = redoCommonDataSchema;
export const initiateRedoDataSchema = redoCommonDataSchema.extend({
    newFormId: z.string().nullish(),
});
// Shared by retractRedoRequest / declineRedoRequest / cancelRedoInProgress.
export const redoReasonDataSchema = z.object({
    formId: z.string().nullish(),
    reason: z.string().nullish(),
});
export const acceptRedoRequestDataSchema = z.object({
    formId: z.string().nullish(),
    assignedUserId: z.string().nullish(),
    newFormId: z.string().nullish(),
});
export const completeRedoDataSchema = z.object({
    formId: z.string().nullish(),
    newFormId: z.string().nullish(),
    originalFormId: z.string().nullish(),
});
// ===== Admissions =====
// recordReasonForNotProceedingMedRec / NoApp share a shape.
export const recordReasonForNotProceedingDataSchema = z.object({
    reason: z.string().nullish(),
    reasonDescription: z.string().nullish(), // PII
    requestType: z.string().nullish(),
    type: z.string().nullish(), // "decline" | "cancel"
});
export const reopenAdmissionDataSchema = z.object({
    reason: z.string().nullish(),
    reasonDescription: z.string().nullish(), // PII
    type: z.string().nullish(), // "reopen"
});
export const autoCloseAdmissionNoMedRecDataSchema = z.object({
    autoClosedAt: z.string().nullish(),
    reason: z.string().nullish(),
});
// Audit type → schema entries for this file, composed in ../registry.ts.
export const workAuditDataRegistry = {
    // Work lifecycle.
    [AuditTrailEntryType.createWorkRequest]: createWorkRequestDataSchema,
    [AuditTrailEntryType.createAmendRequest]: createAmendRequestDataSchema,
    [AuditTrailEntryType.declineWorkRequest]: declineWorkRequestDataSchema,
    [AuditTrailEntryType.acceptWorkRequest]: acceptWorkRequestDataSchema,
    [AuditTrailEntryType.archiveWorkRequest]: archiveWorkRequestDataSchema,
    [AuditTrailEntryType.unarchiveWorkRequest]: emptyWorkDataSchema,
    [AuditTrailEntryType.claimWorkRequest]: claimWorkRequestDataSchema,
    [AuditTrailEntryType.unclaimWorkRequest]: emptyWorkDataSchema,
    [AuditTrailEntryType.finaliseWorkRequest]: emptyWorkDataSchema,
    [AuditTrailEntryType.unfinaliseWorkRequest]: unfinaliseWorkRequestDataSchema,
    [AuditTrailEntryType.expireAmendRequest]: expireAmendRequestDataSchema,
    [AuditTrailEntryType.cancelAmendRequest]: cancelAmendRequestDataSchema,
    [AuditTrailEntryType.cancelWorkRequest]: emptyWorkDataSchema,
    [AuditTrailEntryType.autoRouteToMhlTeam]: autoRouteToMhlTeamDataSchema,
    [AuditTrailEntryType.adminDeleteWorkItem]: adminDeleteWorkItemDataSchema,
    [AuditTrailEntryType.createFileNoteRequest]: createFileNoteRequestDataSchema,
    [AuditTrailEntryType.deleteFileNoteRequest]: deleteFileNoteRequestDataSchema,
    [AuditTrailEntryType.adminScheduleWorkItemExpiry]: emptyWorkDataSchema,
    [AuditTrailEntryType.workItemExpiry]: emptyWorkDataSchema,
    // De minimis corrections.
    [AuditTrailEntryType.createDeMinimisRequest]: createDeMinimisRequestDataSchema,
    [AuditTrailEntryType.acceptDeMinimisRequest]: acceptDeMinimisRequestDataSchema,
    [AuditTrailEntryType.completeDeMinimisRequest]: completeDeMinimisRequestDataSchema,
    [AuditTrailEntryType.rejectDeMinimisRequest]: rejectDeMinimisRequestDataSchema,
    [AuditTrailEntryType.cancelDeMinimisRequest]: cancelDeMinimisRequestDataSchema,
    // Redo admission forms (non-s15).
    [AuditTrailEntryType.createRedoRequest]: createRedoRequestDataSchema,
    [AuditTrailEntryType.initiateRedo]: initiateRedoDataSchema,
    [AuditTrailEntryType.retractRedoRequest]: redoReasonDataSchema,
    [AuditTrailEntryType.declineRedoRequest]: redoReasonDataSchema,
    [AuditTrailEntryType.cancelRedoInProgress]: redoReasonDataSchema,
    [AuditTrailEntryType.acceptRedoRequest]: acceptRedoRequestDataSchema,
    [AuditTrailEntryType.completeRedo]: completeRedoDataSchema,
    // Admissions.
    [AuditTrailEntryType.recordReasonForNotProceedingMedRec]: recordReasonForNotProceedingDataSchema,
    [AuditTrailEntryType.recordReasonForNotProceedingNoApp]: recordReasonForNotProceedingDataSchema,
    [AuditTrailEntryType.reopenAdmission]: reopenAdmissionDataSchema,
    [AuditTrailEntryType.autoCloseAdmissionNoMedRec]: autoCloseAdmissionNoMedRecDataSchema,
};
//# sourceMappingURL=work.js.map